import React, { useContext, useState, useRef, useMemo, forwardRef } from 'react'
import { TextField, Autocomplete, IconButton } from "@mui/material"
import { LoadingContext } from '../M_Utils'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import PasswordChecklist from "react-password-checklist"
import { useTranslation } from 'react-i18next'

const M_InputTextField = forwardRef(({ label, type, value, onChange, disabled, maxNumbers,
    required, color, secondColor, size, placeholder, InputProps, onKeyDown, height,
    padding, margin, backgroundColor, options, labelFields, onChangeValue, iconStart,
    iconEnd, autoFocus, onEnter, helperTextMessage, errorMessage, passVerification,
    minLengthverification, valueAgain, noAutoShrink, disabledCheckPasswords, inputHeight,
    disabledBorderColor, minDate, maxDate, onBlur }, ref) => {

    const isRequired = required || false
    const ctx = useContext(LoadingContext)
    const [showPassword, setShowPassword] = useState(false)
    const { t } = useTranslation()
    const element = useRef(null)

    color = color || ctx.primaryColor || 'var(--color-black)'
    secondColor = secondColor || ctx.secondaryColor || 'var(--color-black)'
    backgroundColor = backgroundColor || 'var(--color-white)'

    const handleChange = (e) => {
        let value = e.target.value

        // Permite apenas números, e no máximo 1 ponto ou vírgula
        if (type === 'decimal') {
            // Regex: números + um único ponto ou vírgula
            if (!/^\d*[.,]?\d*$/.test(value)) return
        }

        if (type === 'number') {
            // permite: "", "-", "+", "-10", "+5", "123"
            if (!/^[+-]?\d*$/.test(value)) return
        }

        onChange?.({
            ...e,
            target: {
                ...e.target,
                value
            }
        })
    }

    const handleBlur = (e) => {
        if (type === 'decimal') {
            const normalized = e.target.value.replace(',', '.')
            onChange?.({
                ...e,
                target: {
                    ...e.target,
                    value: normalized
                }
            })
        }
        onBlur?.(e)
    }

    const styles = {
        margin: margin,
        width: size || null,
        '& label': {
            color: 'var(--color-black)',
            fontWeight: 'bold',
        },
        '& label.Mui-focused': {
            color: 'var(--color-black)',
            fontWeight: disabled && disabledBorderColor ? 'bold' : 'auto'
        },
        '& .MuiInput-underline:after': { borderBottomColor: disabled ? disabledBorderColor : color },
        "& .MuiInputBase-root.Mui-disabled": {
            "& > fieldset": {
                borderColor: disabled ? disabledBorderColor : 'rgba(0, 0, 0, 0.38) !important'
            },
            "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: disabled ? disabledBorderColor : secondColor,
                fontWeight: disabled && disabledBorderColor ? 'bold' : 'auto'
            },
        },
        '& .MuiOutlinedInput-input': {
            backgroundColor: backgroundColor,
            padding: padding,
            height: inputHeight || 'auto',
        },
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: disabled ? disabledBorderColor : 'rgba(0, 0, 0, 0.38) !important',
                width: size || 'auto',
                height: height,
            },
            '&:hover fieldset': {
                borderColor: disabled ? disabledBorderColor : color,
            },
            '&.Mui-focused fieldset': {
                borderColor: disabled ? disabledBorderColor : color,
                boxShadow: `2px 2px 2px rgba(0, 0, 0, 0.25) !important`,
            },
        },
    }

    const autocompleteTextField = useMemo(() => {
        return <Autocomplete
            noOptionsText={t("NoOptions")}
            options={options || []}
            filterOptions={(x) => x}
            sx={styles}
            getOptionLabel={(e) => {
                let resultString = ''
                labelFields.forEach(obj => {
                    if (resultString === '') {
                        resultString = resultString.concat(`${e[obj] || ''}`)
                    } else {
                        resultString = resultString.concat(`, ${e[obj] || ''}`)
                    }
                })
                return resultString.trim()
            }}
            value={value || null}
            onChange={onChangeValue}
            disabled={disabled}
            renderInput={(params) => <TextField
                {...params}
                InputLabelProps={{ shrink: true }}
                onChange={onChange}
                margin="dense"
                required={isRequired}
                label={label}
                placeholder={placeholder}
                variant="outlined"
                InputProps={!disabled ? { ...params.InputProps, ...InputProps } : {}}
                style={{ height: height, textOverflow: 'ellipsis' }}
                sx={styles}
                size="small"
                ref={ref}
            />
            }
        />
    }, [options, value, labelFields, onChangeValue, onChange, isRequired, label, placeholder, styles])

    const textField = useMemo(() => {
        return <TextField
            error={errorMessage}
            helperText={helperTextMessage}
            sx={styles}
            autoFocus={autoFocus}
            placeholder={placeholder}
            InputLabelProps={!noAutoShrink ? { autoComplete: "none", shrink: true } : { autoComplete: "none" }}
            inputProps={{
                maxLength: maxNumbers,
                min: minDate,
                max: maxDate,
                inputMode: type === 'decimal' ? 'decimal' : type === 'number' ? 'numeric' : undefined,
            }}

            InputProps={{
                startAdornment: (iconStart ? iconStart : ''),
                endAdornment: (!disabled && iconEnd ? iconEnd : type === 'password' ?
                    <IconButton
                        disableRipple
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((prev => !prev))}
                        edge="end"
                        sx={{
                            "&.Mui-focusVisible": {
                                color: "var(--color-black)",
                                backgroundColor: "var(--color-grey-light)"
                            },
                        }}
                    >
                        {showPassword ? <VisibilityOff style={{ color: 'var(--color-grey)' }} /> : <Visibility style={{ color: 'var(--color-grey)' }} />}
                    </IconButton> : '')
            }}
            ref={ref}
            required={isRequired}
            margin="dense"
            label={label}
            variant="outlined"
            fullWidth
            type={type === 'number' || type === 'decimal'
                ? 'text'
                : type === 'password'
                    ? showPassword ? 'text' : 'password'
                    : type
            }
            disabled={disabled}
            value={value}
            onChange={handleChange}
            onKeyDown={(e) => {
                if (onEnter && e.key === 'Enter') {
                    onEnter()
                }
                if (e.key === 'Escape' || e.key === 'Enter') {
                    if (iconStart) {
                        element.current.childNodes[1].childNodes[1].blur()
                    } else {
                        element.current.childNodes[1].childNodes[0].blur()
                    }
                    element.current.blur()
                }
                if (onKeyDown) {
                    onKeyDown(e)
                }
            }}
            style={{ height: height }}
            size="small"
            onBlur={handleBlur}
        />
    }, [errorMessage, helperTextMessage, styles, autoFocus, placeholder, maxNumbers, minDate, maxDate, iconStart, iconEnd, element, isRequired, label, disabled, value, handleChange, height, onBlur, showPassword])

    return (
        <>{options ?
            <>
                {autocompleteTextField}
            </> :
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                {textField}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    {type === 'password' && passVerification ?
                        <PasswordChecklist
                            rules={disabledCheckPasswords ?
                                ["minLength", "specialChar", "number", "capital", "lowercase"] :
                                ["minLength", "specialChar", "number", "capital", "lowercase", "match"]}
                            minLength={minLengthverification}
                            value={value}
                            valueAgain={valueAgain}
                            messages={{
                                minLength: (<span> {t("LabelMaxLength")} {minLengthverification} {t("Labelcaracters")} </span>),
                                specialChar: (<span> {t("LabelSpecialCaracts")} </span>),
                                number: (<span> {t("LabelNeedNumber")} </span>),
                                capital: (<span> {t("LabelUpCarac")} </span>),
                                lowercase: (<span> {t("LabelLowerCase")} </span>),
                                ...(disabledCheckPasswords ? {} : { match: (<span> {t("LabelPassMatch")} </span>) })
                            }}
                        />
                        : <></>
                    }
                </div>
            </div>
        }
        </>
    )
})
export { M_InputTextField }
